'use client';
import { useDisclosure } from '@heroui/react';
import Image from 'next/image';
import React from 'react';
import ModalTemplate from './modal-template';
import { useTranslations } from 'next-intl';
import { useCustomizationContext } from '../customizations-provider';
import useCustomerStore from '../store/use-customer-store';
import { FooterProps } from './footer';

export default function FooterContent({
  links,
  isGermanCustomer,
}: FooterProps) {
  const { isOpen, onOpenChange, onOpen } = useDisclosure();
  const tLinks = useTranslations('links');
  const t = useTranslations('footer');
  const logoLink = tLinks('logo');
  const isGreens = useCustomerStore((state) => state.isGreensCustomer);
  const c = useCustomizationContext();
  const logo = c('customer.logo');
  const eControlLogo = c('eControl.logo');

  const rightsReserved = (
    <div className="flex flex-col md:flex-row md:gap-8 justify-items-start pt-10">
      <span className="whitespace-nowrap">{t('copyright')}</span>
      <span className="whitespace-nowrap">{t('rightsReserved')}</span>
    </div>
  );

  return (
    <div
      className={
        isGreens
          ? 'container mx-auto flex flex-col lg:flex-row justify-between'
          : 'padded-container flex flex-col lg:flex-row gap-x-8'
      }
    >
      <div className={isGreens ? 'basis-[48%]' : 'flex-1 flex-col'}>
        {isGreens ? (
          <div>
            <span className="font-semibold text-lg leading-8  mb-2 mr-4">
              {t.rich('initiativeBy', {
                BB1: () => (
                  <a
                    data-testid="backbone-logo"
                    href="https://www.backbone.one"
                    title="backbone.one Website"
                    target="_blank"
                  >
                    <Image
                      src="/images/bb1-logo-with-text.svg"
                      alt="backbone.one logo"
                      width="243"
                      height="36"
                      className="mb-2 mr-4 inline-block h-9 w-auto"
                      priority
                    />
                  </a>
                ),
                MAXENERGY: () => (
                  <a
                    data-testid="max-energy-logo"
                    href="https://www.maxenergy.at"
                    className="mb-2 ml-4"
                    title="MAXENERGY Website"
                    target="_blank"
                  >
                    <Image
                      src="/images/maxenergy-logo.svg"
                      alt="MaxEnergy logo"
                      width="96"
                      height="36"
                      className="mb-2 mx-2 inline-block h-9 w-auto"
                      priority
                    />
                  </a>
                ),
              })}
            </span>

            <p
              data-testid="bb1-description"
              className="mt-5 md:mt-10 mb-10 lg:mb-0"
            >
              {t('bb1Description')}
            </p>
            <div className="hidden lg:flex">{rightsReserved}</div>
          </div>
        ) : (
          <a data-testid="footer-logo" href={logoLink}>
            <Image
              alt={logo.description}
              src={logo.url}
              width={logo.width}
              height={logo.height}
              className="h-10 w-auto mb-10 lg:mb-0"
            />
          </a>
        )}
      </div>
      <div
        className={
          isGreens
            ? 'flex flex-col basis-[45%] md:pt-10 lg:pt-0 md:justify-between'
            : 'flex-1 flex-col'
        }
      >
        {!isGermanCustomer && (
          <div>
            <div className="flex flex-row items-center">
              <a
                data-testid="e-control-logo"
                href={t('econtrolLink')}
                target="_blank"
              >
                <Image
                  alt="econtrol logo"
                  src={eControlLogo.url}
                  width={50}
                  height={50}
                  className="mr-5"
                />
              </a>
              <div>
                <p className="block">{t('econtrolTitle')}</p>
                <p
                  data-testid="disclaimer-link"
                  className="font-semibold cursor-pointer"
                  onClick={onOpen}
                >
                  {t('disclaimerTitle')}
                </p>
                <ModalTemplate
                  isOpen={isOpen}
                  onOpenChange={onOpenChange}
                  title={t('disclaimerTitle')}
                >
                  {t('disclaimerInfo')}
                </ModalTemplate>
              </div>
            </div>
            <p data-testid="estimation-info" className="!pt-5 lg:pt-0">
              {t('estimationInfo')}
            </p>
          </div>
        )}

        <div
          className={`flex flex-row text-nowrap underline underline-offset-4 gap-4 xl:gap-10 ${isGermanCustomer ? 'lg:pl-40' : '!pt-10'}`}
        >
          {isGreens && (
            <a data-testid="faq-link" href={links.faq}>
              {t('links.faq')}
            </a>
          )}
          <a
            data-testid="impressum-link"
            href={tLinks('impressum')}
            target="_blank"
          >
            {t('links.impressum')}
          </a>
          <a
            data-testid="data-privacy-link"
            href={tLinks('privacyPolicy')}
            target="_blank"
          >
            {t('links.privacyPolicy')}
          </a>
        </div>
      </div>
      {isGreens && <div className="lg:hidden">{rightsReserved}</div>}
    </div>
  );
}
